/* ==========================================================================
   dt-donate.js
   Progressive enhancement for the DT Donate widget (sections/dt-donate-widget).

   The widget is a real <form method="post" action="/cart/add">. Without this
   file it still works: the amount radios set `quantity`, the Gift Aid radios
   set `id` (the variant), and `return_to` sends the visitor to the cart page or
   straight to the checkout. This file adds the custom amount field, the live
   button label, and an AJAX add so the page does not flash.

   With the section set to "Show the cart and stay on the page" the add opens
   the theme's own cart UI (Refresh renders a <cart-drawer> or a
   <cart-notification>, depending on the theme's Cart type setting) using the
   stock pattern: POST /cart/add.js with a `sections` list, then hand the
   response to that element's renderContents. No stock file is forked.

   Vanilla JS, no libraries, everything namespaced dtDonate / data-dt-*.
   bychamberlain, 2026.
   ========================================================================== */

(function () {
  'use strict';

  if (window.dtDonateLoaded) return;
  window.dtDonateLoaded = true;

  var MIN_AMOUNT = 1;

  // The donation is a quantity of a 1 pound variant, so only whole pounds can be
  // given. A typed 12.99 is rounded rather than silently truncated to 12, and
  // normaliseCustom writes the rounded figure back into the field so the visitor
  // sees exactly what they are about to give.
  function currentAmount(form) {
    var custom = form.querySelector('[data-dt-custom-input]');
    if (custom && custom.value !== '') {
      var typed = parseFloat(custom.value);
      return isNaN(typed) ? 0 : Math.round(typed);
    }
    var checked = form.querySelector('input[name="quantity"]:checked');
    return checked ? parseInt(checked.value, 10) : 0;
  }

  function normaliseCustom(form) {
    var custom = form.querySelector('[data-dt-custom-input]');
    if (!custom || custom.value === '') return;
    var typed = parseFloat(custom.value);
    if (isNaN(typed)) return;
    var rounded = Math.max(MIN_AMOUNT, Math.round(typed));
    if (String(rounded) !== custom.value) custom.value = String(rounded);
  }

  function currentVariant(form) {
    var checked = form.querySelector('input[name="id"]:checked');
    if (checked) return checked.value;
    var single = form.querySelector('input[name="id"]');
    return single ? single.value : '';
  }

  function giftAidOn(form) {
    var checked = form.querySelector('input[name="id"]:checked');
    return checked ? checked.getAttribute('data-dt-gift-aid') === 'true' : false;
  }

  // The error paragraph is always in the DOM and always in the accessibility
  // tree: a live region that was hidden at the moment its text changed is not
  // reliably announced. Setting the text on the next tick gives screen readers a
  // change to observe. Empty, the paragraph has no height, so nothing shifts.
  function setError(form, message) {
    var box = form.querySelector('[data-dt-donate-error]');
    if (!box) return;
    box.hidden = false;
    if (!message) {
      box.textContent = '';
      return;
    }
    box.textContent = '';
    window.setTimeout(function () {
      box.textContent = message;
    }, 50);
  }

  function updateLabel(form) {
    var button = form.querySelector('[data-dt-donate-submit]');
    if (!button) return;
    var base = button.getAttribute('data-dt-label') || 'Donate';
    var suffix = button.getAttribute('data-dt-gift-aid-suffix') || '';
    var symbol = form.getAttribute('data-dt-currency-symbol') || '';
    var amount = currentAmount(form);
    var text = base;

    if (amount >= MIN_AMOUNT) {
      text += ' ' + symbol + amount;
    }
    if (giftAidOn(form) && suffix !== '') {
      text += ' · ' + suffix;
    }
    button.textContent = text;
  }

  function clearPresets(form) {
    var presets = form.querySelectorAll('input[name="quantity"]');
    for (var i = 0; i < presets.length; i++) {
      presets[i].checked = false;
    }
  }

  // The theme's own cart UI, whichever it is running: <cart-notification> in
  // notification mode, <cart-drawer> in drawer mode, neither on the cart page
  // or if the theme is set to Cart page. Same lookup order as stock
  // product-form.js, so we never force a drawer the theme is not rendering.
  function themeCart() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function submitAjax(form) {
    var button = form.querySelector('[data-dt-donate-submit]');
    normaliseCustom(form);
    var amount = currentAmount(form);
    var variant = currentVariant(form);

    if (!amount || amount < MIN_AMOUNT) {
      setError(form, form.getAttribute('data-dt-error-amount') || 'Please choose an amount.');
      return;
    }
    if (!variant) {
      setError(form, form.getAttribute('data-dt-error-generic') || 'Sorry, something went wrong.');
      return;
    }

    setError(form, '');
    if (button) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    }

    // Only ask for the section HTML when the cart UI is actually going to be
    // shown, and only for the sections that element says it renders. Asking for
    // sections the theme does not have on the page would just waste the render.
    var showCart = form.getAttribute('data-dt-after') !== 'checkout' &&
      form.getAttribute('data-dt-after') !== 'cart_page';
    var cart = showCart ? themeCart() : null;

    // Single item rather than an items array: the single-item response carries
    // the line item `key`, which <cart-notification> needs to find the product
    // section it just rendered. The drawer is happy with either.
    var payload = { id: variant, quantity: amount };
    if (cart) {
      payload.sections = cart.getSectionsToRender().map(function (section) {
        return section.id;
      }).join(',');
      payload.sections_url = window.location.pathname;
      if (typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(document.activeElement);
      }
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || 'Add to cart failed');
          }
          return data;
        });
      })
      .then(function (data) {
        if (!cart) {
          window.location.href = form.getAttribute('data-dt-redirect') || '/cart';
          return;
        }

        // The empty-cart state is set on both the element and its inner wrapper;
        // stock product-form.js clears the outer one, renderContents the inner.
        cart.renderContents(data);
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');

        // The drawer is now open over the page, so the button has to come back
        // to life: a second donation is a legitimate thing to want, and each
        // click simply adds more of the 1 pound variant.
        releaseButton(form);
      })
      .catch(function (error) {
        releaseButton(form);
        setError(form, error.message || form.getAttribute('data-dt-error-generic') || 'Sorry, something went wrong.');
      });
  }

  // Clears the double-submit guard and puts the label back to whatever is
  // selected now, since the label was left mid-submit.
  function releaseButton(form) {
    var button = form.querySelector('[data-dt-donate-submit]');
    if (button) {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
    updateLabel(form);
  }

  function initForm(form) {
    if (form.getAttribute('data-dt-donate-ready') === 'true') return;
    form.setAttribute('data-dt-donate-ready', 'true');

    // The old donate links preselect gift aid via ?variant=<id> (the two
    // variants of the donation product). Honour them: if the URL names one of
    // this form's gift aid variant ids, check that radio before first paint.
    try {
      var urlVariant = new URLSearchParams(window.location.search).get('variant');
      if (urlVariant) {
        var match = form.querySelector('input[name="id"][value="' + urlVariant + '"]');
        if (match) match.checked = true;
      }
    } catch (e) {
      // URLSearchParams missing: fine, the section default stands.
    }

    // The custom amount field is hidden in the markup so that visitors without
    // JavaScript are never shown a field that cannot be submitted.
    var customField = form.querySelector('[data-dt-custom-field]');
    if (customField) customField.hidden = false;

    // Whichever preset the section rendered as checked is the one to fall back
    // to, so clearing the custom field never leaves the card with no amount.
    // Once the visitor picks a preset of their own, that becomes the fallback,
    // so emptying the custom field returns them to their own last choice.
    var lastPreset = form.querySelector('input[name="quantity"]:checked') ||
      form.querySelector('input[name="quantity"]');

    var customInput = form.querySelector('[data-dt-custom-input]');
    if (customInput) {
      customInput.addEventListener('input', function () {
        if (customInput.value === '') {
          if (lastPreset) lastPreset.checked = true;
        } else {
          clearPresets(form);
        }
        setError(form, '');
        updateLabel(form);
      });
      // Rounding happens on change, not on every keystroke, so a half-typed
      // "12." is not rewritten under the visitor's cursor.
      customInput.addEventListener('change', function () {
        normaliseCustom(form);
        updateLabel(form);
      });
    }

    var presets = form.querySelectorAll('input[name="quantity"]');
    for (var i = 0; i < presets.length; i++) {
      presets[i].addEventListener('change', function () {
        lastPreset = this;
        if (customInput) customInput.value = '';
        setError(form, '');
        updateLabel(form);
      });
    }

    // Same signal stock product-form.js gives: the button opens a dialog rather
    // than navigating. Drawer only, as the notification popup is not a dialog.
    var submit = form.querySelector('[data-dt-donate-submit]');
    if (submit && form.getAttribute('data-dt-after') === 'cart' && document.querySelector('cart-drawer')) {
      submit.setAttribute('aria-haspopup', 'dialog');
    }

    var giftAid = form.querySelectorAll('input[name="id"]');
    for (var j = 0; j < giftAid.length; j++) {
      giftAid[j].addEventListener('change', function () {
        updateLabel(form);
      });
    }

    form.addEventListener('submit', function (event) {
      // No fetch means no AJAX path: let the native form POST take over.
      if (!window.fetch) return;
      event.preventDefault();
      submitAjax(form);
    });

    updateLabel(form);
  }

  function init(root) {
    var scope = root || document;
    var forms = scope.querySelectorAll('[data-dt-donate]');
    for (var i = 0; i < forms.length; i++) {
      initForm(forms[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: re-run when a section is added or re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
